"use client";

import { CheckCircle2, Clock, Mail, MessageSquare, Users } from "lucide-react";
import { Dialog, DialogContent } from "../../shadcn/ui/dialog";

import { PunchupButton } from "../common/PunchupButton";
import { SuccessModalData } from "./types";
import { buildSuccessDisplayData } from "./constants";
import { USE_CASE_ICONS_SM } from "./UseCaseCards";
import { motion } from "motion/react";

interface MessageScheduleSuccessModalProps {
  data: SuccessModalData | null;
  onDone: () => void;
}

export function MessageScheduleSuccessModal({
  data,
  onDone,
}: MessageScheduleSuccessModalProps) {
  if (!data) return null;

  const {
    isScheduled,
    title,
    config,
    totalUsersToReach,
    fanText,
    channelSuffix,
    scheduleLabel,
    truncatedSubject,
  } = buildSuccessDisplayData(data);

  return (
    <Dialog open={!!data} onOpenChange={() => onDone()}>
      <DialogContent className="max-h-[90vh] max-w-md gap-0 overflow-y-auto p-0">
        {/* Header banner */}
        <motion.div
          className="relative py-8 pl-4 pr-12"
          initial={{ y: -100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.4, type: "spring" }}
        >
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
          >
            <CheckCircle2 className="mb-3 h-12 w-12 text-primary" />
          </motion.div>

          <motion.h2
            className="mb-1 text-2xl font-semibold text-foreground"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            {title}
          </motion.h2>
          <motion.p
            className="break-words text-sm text-muted-foreground"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
          >
            {isScheduled
              ? `Your message will be sent at ${scheduleLabel}`
              : "Your message is being sent now"}
          </motion.p>
        </motion.div>

        {/* Body section */}
        <div className="bg-card px-4 py-6">
          <div className="mb-6 space-y-4">
            {/* Use case */}
            <div className="flex items-start gap-3">
              <span className="mt-0.5 text-primary">
                {USE_CASE_ICONS_SM[data.useCaseType]}
              </span>
              <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-xs text-muted-foreground">
                  Message Type
                </p>
                <p className="text-sm">{config.title}</p>
              </div>
            </div>

            {/* Audience */}
            <div className="flex items-start gap-3">
              <Users className="mt-0.5 h-4 w-4 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-xs text-muted-foreground">Audience</p>
                <p className="text-sm">
                  {totalUsersToReach.toLocaleString()} {fanText}
                  {channelSuffix}
                </p>
              </div>
            </div>

            {/* Schedule */}
            <div className="flex items-start gap-3">
              <Clock className="mt-0.5 h-4 w-4 text-primary" />
              <div className="min-w-0 flex-1">
                <p className="mb-0.5 text-xs text-muted-foreground">
                  Scheduled For
                </p>
                <p className="text-sm">{scheduleLabel}</p>
              </div>
            </div>

            {/* Email subject */}
            {data.emailEnabled && truncatedSubject && (
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-4 w-4 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="mb-0.5 text-xs text-muted-foreground">
                    Email Subject
                  </p>
                  <p className="break-words text-sm text-muted-foreground">
                    {truncatedSubject}
                  </p>
                </div>
              </div>
            )}

            {/* SMS indicator */}
            {data.smsEnabled && !data.emailEnabled && (
              <div className="flex items-start gap-3">
                <MessageSquare className="mt-0.5 h-4 w-4 text-primary" />
                <div className="min-w-0 flex-1">
                  <p className="mb-0.5 text-xs text-muted-foreground">
                    Channel
                  </p>
                  <p className="text-sm text-muted-foreground">SMS message</p>
                </div>
              </div>
            )}
          </div>

          {/* CTA */}
          <PunchupButton onClick={onDone} className="w-full">
            Done
          </PunchupButton>
        </div>
      </DialogContent>
    </Dialog>
  );
}
